import { Formik, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import Link from "../../../../components/Link";
import Submit from "../../../../components/FormikForm/Submit";
import { Group } from "../../../../types";
import Grid from "../../../../components/Grid";
import {
  initialValues,
  schema,
} from "../../../../constants/forms/sessions/duplicate";
import { duplicateSession, resetDuplicateSession } from "../../../../store/actions/session";
import { setToZeroTimeZone } from "../../../../helpers/dates-hanlder";
import SessionForm from "../components/SessionForm";
import { getGroupById } from "../../../../store/actions/group";
import { SessionInDto } from "../../../../types/session.dto";
import Snackbar from "../../../../components/Snackbar";
import { useLayout } from "../../../../providers/LayoutProvider";
import moment from "moment-timezone";
import { inputDateTimeLocal } from "../../../../helpers/dates-hanlder";
import { getOneSession } from "../../../../store/actions/session";
import Loader from "../../../../components/LogoLoader";
import { useFileUploadProgress } from "../../../../providers/FileUploader";
import { hideModal, showModal } from "../../../../store/actions/modal";
import ModalWaitAMinuteBis from "../../Posts/New/components/ModalWaitAMinute";

interface ComponentProps {
  groupInformation: Group;
  groupStates: {
    loading: boolean;
    success: boolean;
    error: boolean | string;
  };
  sessionInformation: SessionInDto;
  sessionStates: {
    loading: boolean;
    success: boolean;
    error: boolean | string;
  };
  duplicateSessionInformation: SessionInDto;
  duplicateSessionStates: {
    loading: boolean;
    success: boolean;
    error: boolean | string;
  };
  userInformation: any;
  match;
}

// Hook para escuchar cambios en session_datetime y actualizar reminders
const AutoUpdateReminders = () => {
  const { values, setFieldValue } = useFormikContext<any>();

  useEffect(() => {
    if (values?.session_datetime && values?.session_reminders_settings) {
      values.session_reminders_settings.forEach((setting, index) => {
        // Para first_announcement con execution_type "now"
        if (setting.type === "first_announcement" && setting.execution_type === "now") {
          setFieldValue(`session_reminders_settings.${index}.exec_datetime`, values.session_datetime);
        }
        // Para open_room
        if (setting.type === "open_room") {
          setFieldValue(`session_reminders_settings.${index}.exec_datetime`, values.session_datetime);
        }
      });
    }
  }, [values?.session_datetime, setFieldValue]);

  return null; // No renderiza nada
};

const Component = ({
  groupInformation,
  groupStates,
  sessionInformation,
  sessionStates,
  duplicateSessionInformation,
  duplicateSessionStates,
  userInformation,
  match,
}: ComponentProps) => {
  const { layout, setLayout } = useLayout();
  const { uploadFiles, resetFileUploadProgress } = useFileUploadProgress();
  const dispatch = useDispatch();
  const history = useHistory();
  const [groupId, setGroupId] = useState<number>();
  const [formSubmmited, setFormSubmmited] = useState(false);
  const [sessionToDuplicateId, setSessionToDuplicateId] = useState<number | null>(null);

  // Function to create initial values from a session to duplicate
  const createInitialValuesFromSession = (session: SessionInDto) => {
    if (!session) return initialValues;

    console.log('session', session);
    // Convert duration based on type (same logic as Edit)
    const duration = session.duration_type === 'hour'
      ? session.duration
      : Math.round(session.duration / 60 * 10) / 10; // Convert minutes to hours with 1 decimal

    return {
      ...initialValues,
      title: session.title ? `${session.title} (Copia)` : '',
      description: session.description || '',
      join_url: session.join_url || '',
      speaker_name: session.speaker_name || '',
      speaker_profile_pic: session.speaker_profile_pic ? [session.speaker_profile_pic] : [],
      use_profile_info: session.use_profile_info ? ['true'] : ['false'],
      cover_pic: session.cover_pic ? [session.cover_pic] : [],
      complementary_material_description: session.complementary_material_description || '',
      complementary_material_files: session.complementary_material_files?.map(file => ({
        id: file.id, // Solo guardamos el ID para duplicar después
        name: file.name || file.original_name,
        original_name: file.original_name,
        mime: file.mime,
        size: file.size
      })) || [],
      complementary_material_downloadable: session.complementary_material_downloadable ? ['true'] : ['false'],
      session_datetime: "", // Use same helper as Edit
      content_categories: session.content_categories?.map(category => category.id) || [], // Map to IDs like Edit
      recorded_session_url: '',
      group: session.group?.id || groupId,
      will_be_recorded: session.will_be_recorded ? ['true'] : ['false'],
      timezones_to_show: session.timezones_to_show || [], // Add missing field
      access_code: session.access_code || '',
      duration: duration,
      duration_type: session.duration_type || 'hour',
      recorded_access_code: '', // Reset for new session
      notificate_by_chat: session.notificate_by_chat ? ['true'] : ['false'],
      notificate_by_email: session.notificate_by_email ? ['true'] : ['false'],
      notify_upload_recording: session.notify_upload_recording ? ['true'] : ['false'], // Add missing field
      recording_expiration_datetime: '', // Add missing field with proper date conversion
      session_reminders_settings: session.session_reminders_settings?.map(sett => {
        console.log('Reminder setting original:', sett);
        console.log('Reminder setting new:', {
          exec_time_before: sett.exec_time_before,
          exec_time_before_unit: sett.exec_time_before_unit,
          enabled: sett?.enabled ? ['true'] : [], // Conversión explícita de boolean a array
          custom_message: sett.custom_message || "",
          execution_type: sett.execution_type,
          type: sett.type,
          exec_datetime: null, // Se recalculará automáticamente cuando se seleccione session_datetime
          schedule_type: sett.schedule_type
        });
        return {
          exec_time_before: sett.exec_time_before,
          exec_time_before_unit: sett.exec_time_before_unit,
          enabled: sett?.enabled ? ['true'] : [], // Conversión explícita de boolean a array
          custom_message: sett.custom_message || "",
          execution_type: sett.execution_type,
          type: sett.type,
          exec_datetime: null, // Se recalculará automáticamente cuando se seleccione session_datetime
          schedule_type: sett.schedule_type
        };
      }) || initialValues.session_reminders_settings,
    };
  };

  const onSubmit = async ({ values, actions }) => {
    const {
      speaker_name,
      speaker_profile_pic,
      notify_upload_recording,
      session_datetime,
      will_be_recorded,
      notificate_by_chat,
      recording_expiration_datetime,
      notificate_by_email,
      complementary_material_files,
      ...rest
    } = values;
    const useProfileInfo = values.use_profile_info.length > 0;

    // Verificar si hay archivos para subir
    const thereAreFilesToUpload = values && (
      (values.complementary_material_files?.some(file => file instanceof File))
    );

    if (thereAreFilesToUpload) {
      dispatch(showModal("modal-wait-a-minute-bis"));
    }

    try {
      const remindersSettings = values['session_reminders_settings'].map(remSett => {
        return {
          ...remSett,
          enabled: remSett?.enabled?.includes("true"),
          exec_datetime: remSett?.exec_datetime && remSett?.exec_datetime !== ""
            ? setToZeroTimeZone(remSett.exec_datetime)
            : (remSett?.execution_type === "now" ? setToZeroTimeZone(session_datetime) : null)
        }
      })

      const recordingExpirationDate = (!!recording_expiration_datetime && recording_expiration_datetime !== "")
        ? setToZeroTimeZone(recording_expiration_datetime)
        : null

      if (thereAreFilesToUpload) {
        // Separar archivos nuevos (File instances) de archivos existentes (con id)
        const newFiles = complementary_material_files?.filter(file => file instanceof File) || [];
        const existingFiles = complementary_material_files?.filter(file => file.id && !(file instanceof File)) || [];

        // Subir solo archivos nuevos
        const uploadedFiles = await uploadFiles(
          {
            ...(newFiles.length > 0 && { complementary_material_files: newFiles }),
          },
          userInformation.id,
          "session"
        );

        // Combinar archivos subidos (objetos completos) con archivos existentes (solo ID)
        const allFiles = [
          ...(uploadedFiles?.complementary_material_files || []),
          ...existingFiles.map(file => ({ id: file.id })) // Solo enviar el ID
        ];

        const obj = {
          ...rest,
          ...(!useProfileInfo && { speaker_name, speaker_profile_pic: uploadedFiles?.speaker_profile_pic || values.speaker_profile_pic }),
          use_profile_info: useProfileInfo,
          session_dt_in_local_timezone: session_datetime,
          session_datetime: setToZeroTimeZone(session_datetime),
          recording_expiration_datetime: recordingExpirationDate,
          will_be_recorded: will_be_recorded?.includes("true") || false,
          notificate_by_chat: notificate_by_chat?.includes("true") || false,
          notificate_by_email: notificate_by_email?.includes("true") || false,
          notify_upload_recording: notify_upload_recording?.includes("true") || false,
          complementary_material_downloadable: values.complementary_material_downloadable?.includes("true") || false,
          // Enviar todos los archivos (nuevos como objetos, existentes como {id: X})
          complementary_material_files: allFiles,
          session_reminders_settings: remindersSettings
        }

        // Instead of creating a new session, we duplicate the existing one
        if (sessionToDuplicateId) {
          dispatch(duplicateSession({
            idSession: sessionToDuplicateId,
            ...obj
          }));
        }
      } else {
        // Si no hay archivos para subir, solo enviar IDs de archivos existentes
        const existingFiles = complementary_material_files
          ?.filter(file => file.id && !(file instanceof File))
          .map(file => ({ id: file.id })) || []; // Solo enviar {id: X}

        const obj = {
          ...rest,
          ...(!useProfileInfo && { speaker_name, speaker_profile_pic }),
          use_profile_info: useProfileInfo,
          session_dt_in_local_timezone: session_datetime,
          session_datetime: setToZeroTimeZone(session_datetime),
          recording_expiration_datetime: recordingExpirationDate,
          will_be_recorded: will_be_recorded?.includes("true") || false,
          notificate_by_chat: notificate_by_chat?.includes("true") || false,
          notificate_by_email: notificate_by_email?.includes("true") || false,
          notify_upload_recording: notify_upload_recording?.includes("true") || false,
          complementary_material_downloadable: values.complementary_material_downloadable?.includes("true") || false,
          // Enviar archivos existentes como {id: X}
          complementary_material_files: existingFiles,
          session_reminders_settings: remindersSettings
        }

        // Instead of creating a new session, we duplicate the existing one
        if (sessionToDuplicateId) {
          dispatch(duplicateSession({
            idSession: sessionToDuplicateId,
            ...obj
          }));
        }
      }
    } catch (error) {
      console.error('Error during file uploads:', error);
    } finally {
      dispatch(hideModal());
    }
  };

  useEffect(() => {
    if (!!match?.params) {
      setGroupId(Number(match?.params?.idGroup));
      setSessionToDuplicateId(Number(match?.params?.sessionId));
    }
  }, [match]);

  useEffect(() => {
    if (!!groupId) {
      groupInformation?.id !== groupId &&
        dispatch(getGroupById({ group_id: groupId }));
    }
  }, [groupId, groupInformation]);

  useEffect(() => {
    if (sessionToDuplicateId) {
      dispatch(getOneSession({ idSession: sessionToDuplicateId }));
    }
  }, [sessionToDuplicateId]);

  useEffect(() => {
    if (!!duplicateSessionInformation && duplicateSessionStates.success) {
      setTimeout(() => {
        const groupId = groupInformation?.id;
        const sessionId = duplicateSessionInformation?.id;
        dispatch(resetDuplicateSession());
        resetFileUploadProgress();
        dispatch(hideModal());
        history.push(`/group/${groupId}/s/${sessionId}?new=t`);
      }, 3500);
    }
  }, [duplicateSessionInformation, duplicateSessionStates]);

  useEffect(() => {
    setLayout({
      ...layout,
      page: {
        title: "Duplicar encuentro",
        description: "Duplicar encuentro",
        back: `/dashboard/${groupInformation?.group_type}/${groupInformation?.id}/sessions`,
      },
      dashboard: {
        tabs: {
          visible: false,
        },
      },
      header: {
        visible: true,
        actions: [
          <Link
            style={{ marginRight: "8px" }}
            options={{
              type: "outline",
              size: "lg",
              skin: "purple",
              native: true,
            }}
            disabled={groupStates.loading}
            loading={groupStates.loading}
            href={`/dashboard/${groupInformation?.group_type}/${groupInformation?.id}/sessions`}
          >
            Cancelar
          </Link>,

          <Submit
            isSubmmiting={duplicateSessionStates.loading}
            options={{
              type: "filled",
              size: "lg",
              skin: "purple",
            }}
            form="new-session-form"
          >
            Duplicar
          </Submit>,
        ],
      },
    });
  }, [groupInformation]);

  if (!sessionInformation && sessionStates.loading) {
    return <Loader />;
  }

  return (
    <>
      <Grid.Container>
        <Grid.Row>
          <Grid.Col>
            <p>
              🎥 Duplicá este encuentro para crear una nueva versión con la misma configuración. Podés modificar cualquier campo antes de guardar.
            </p>
          </Grid.Col>
        </Grid.Row>

        <div style={{ height: "32px" }}></div>

        <Grid.Row>
          <Grid.Col lg={6}>
            <Formik
              initialValues={sessionInformation
                ? createInitialValuesFromSession(sessionInformation)
                : {
                  ...initialValues,
                  group: groupId,
                }
              }
              onSubmit={(values, actions) => {
                if (onSubmit) onSubmit({ values, actions });
              }}
              validateOnChange={false}
              validateOnBlur={formSubmmited}
              validationSchema={schema}
              enableReinitialize
            >
              {({ handleSubmit, ...rest }) => {
                console.log('values', rest.values);
                console.log('errors', rest.errors);
                return (
                  <>
                    <AutoUpdateReminders />
                    <form
                      className="theme-form"
                      onSubmit={(event) => {
                        setFormSubmmited(true);
                        handleSubmit(event);
                      }}
                      id="new-session-form"
                    >
                      <SessionForm {...rest} isEdit={false} />
                    </form>
                  </>
                );
              }}
            </Formik>
          </Grid.Col>
        </Grid.Row>

        <Grid.Row>
          <Grid.Col>
            <Link
              style={{ marginRight: "8px" }}
              options={{
                type: "outline",
                size: "lg",
                skin: "purple",
                native: true,
              }}
              disabled={groupStates.loading}
              loading={groupStates.loading}
              href={`/dashboard/${groupInformation?.group_type}/${groupInformation?.id}/sessions`}
            >
              Cancelar
            </Link>

            <Submit
              isSubmmiting={duplicateSessionStates.loading}
              options={{
                type: "filled",
                size: "lg",
                skin: "purple",
              }}
              form="new-session-form"
            >
              Duplicar encuentro
            </Submit>
          </Grid.Col>
        </Grid.Row>
      </Grid.Container>

      <ModalWaitAMinuteBis />

      <Snackbar
        visible={duplicateSessionStates.success || !!duplicateSessionStates.error}
        options={{
          time: 2000,
          type: duplicateSessionStates.success ? "success" : "error",
        }}
      >
        {duplicateSessionStates.success
          ? "Encuentro duplicado."
          : "Error al duplicar el encuentro."}
      </Snackbar>
    </>
  );
};

const state = ({ groupStore, sessionStore, userStore }) => {
  const { data: sessionInformation, states: sessionStates } = sessionStore.one;
  const { data: duplicateSessionInformation, states: duplicateSessionStates } =
    sessionStore.duplicate;
  const { data: groupInformation, states: groupStates } = groupStore.group;
  const { data: userInformation } = userStore.information;

  return {
    groupInformation,
    groupStates,
    sessionInformation,
    sessionStates,
    duplicateSessionInformation,
    duplicateSessionStates,
    userInformation,
  };
};

export default connect(state)(Component);
